import { format, isValid, parse } from 'date-fns';
import { currentLanguage } from 'i18n';

const localeToUse = {
  en: 'en-GB',
  de: 'de-DE',
};

export const CANONICAL_ANCHOR_TIMEZONE = 'Europe/Berlin';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

const BARE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const NOON_HOUR = 12;

/**
 * Formats a calendar date — a value whose meaning is a day, not a moment — as that day,
 * identically for every viewer.
 *
 * Accepts the shapes a calendar date arrives in:
 * - a canonical epoch in seconds (a `*_date` field of a canonical payload), read as its
 *   Europe/Berlin day;
 * - a bare `yyyy-MM-dd` string, shown as written;
 * - a timestamp the backend anchored to a Berlin day (e.g. `due_date`
 *   `2025-08-15T00:00:00+02:00`), read as its Europe/Berlin day.
 *
 * Returns '' for empty or unparseable input.
 */
export const formatCalendarDay = (value?: number | string, locale = currentLanguage): string => {
  if (!value) {
    return '';
  }
  if (typeof value === 'number') {
    return formatBerlinDay(new Date(value * 1000), locale);
  }
  if (BARE_DAY_PATTERN.test(value)) {
    return formatBareDay(value, locale);
  }
  return formatBerlinDay(value, locale);
};

/**
 * Formats the day on which a moment happened, as the viewer experienced it (in the browser's
 * timezone) — e.g. the day of `created_at` or `go_live_date`. Use `formatDayAndTime` to also
 * show the time.
 *
 * Does not accept a number: canonical epochs are calendar dates (see `formatCalendarDay`).
 *
 * Returns '' for empty or unparseable input.
 */
export const formatInstantAsDay = (value?: string | Date, locale = currentLanguage): string => {
  if (!value) {
    return '';
  }
  return formatDateTime(value, DATE_FORMAT_OPTIONS, locale);
};

export const formatDayAndTime = (date: string | number | Date, locale = currentLanguage): string => {
  return formatDateTime(
    date,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
    locale,
  );
};

export const formatTimestamp =
  <T>(formatFunction: (args: Date) => T) =>
  (timestamp?: number): T | undefined => {
    if (!timestamp) {
      return undefined;
    }

    const date = new Date(timestamp * 1000);
    if (!date || isNaN(date.getTime())) {
      return undefined;
    }

    return formatFunction(date);
  };

export const formatDateToISO = (date: Date, timeZone?: string): string => {
  if (!timeZone) {
    return format(date, 'yyyy-MM-dd');
  }
  const parts = new Intl.DateTimeFormat('en-GB', { ...DATE_FORMAT_OPTIONS, timeZone }).formatToParts(date);
  const partValue = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value;
  return `${partValue('year')}-${partValue('month')}-${partValue('day')}`;
};

//https://www.notion.so/procuros/Dates-Timestamps-Timezones-904a5168105c497ebef01d929b29309c?pvs=4
export const fromStringDateToProcurosTimestamp = (isoDay: string): number => {
  if (!BARE_DAY_PATTERN.test(isoDay)) {
    return NaN;
  }
  const [year, month, day] = isoDay.split('-').map(Number);
  const monthIndex = month - 1;
  const utcNoonInMs = Date.UTC(year, monthIndex, day, NOON_HOUR);
  return (utcNoonInMs - getTimezoneOffsetMs(utcNoonInMs, CANONICAL_ANCHOR_TIMEZONE)) / 1000;
};

export function diffInMinutes(date1: string, date2 = new Date(), absolute = true) {
  return Math.floor(diffInSeconds(date1, date2, absolute) / 60);
}

export function diffInSeconds(date1: string | number | Date, date2 = new Date(), absolute = true) {
  const diff = new Date(date1).getTime() - new Date(date2).getTime();
  const seconds = Math.floor(diff / 1000);

  return absolute ? Math.abs(seconds) : seconds;
}

export const formatISOForFilename = (date: Date = new Date()): string => {
  return date.toISOString().replace(/[:.]/g, '-');
};

export const parseFlexibleDate = (dateString: string) => {
  const formats = ['dd/MM/yyyy', 'yyyy/MM/dd', 'dd-MM-yyyy', 'yyyy-MM-dd', 'dd.MM.yyyy', 'yyyy.MM.dd'];

  for (const format of formats) {
    const date = parse(dateString, format, new Date());
    if (isValid(date)) {
      return date;
    }
  }

  throw new Error('Unable to parse date with any known format');
};

const DAYS_PER_YEAR = 365;
const DAYS_PER_MONTH = 30;
const DAYS_PER_WEEK = 7;

export const convertDaysToTimeUnit = (days: number): { quantity: number; unit: string } => {
  if (days >= DAYS_PER_YEAR) {
    return { quantity: Math.floor(days / DAYS_PER_YEAR), unit: 'year' };
  }
  if (days >= DAYS_PER_MONTH) {
    return { quantity: Math.floor(days / DAYS_PER_MONTH), unit: 'month' };
  }
  if (days >= DAYS_PER_WEEK) {
    return { quantity: Math.floor(days / DAYS_PER_WEEK), unit: 'week' };
  }
  return { quantity: Math.floor(days), unit: 'day' };
};

const formatDateTime = (
  str: string | number | Date,
  options: Intl.DateTimeFormatOptions,
  locale = currentLanguage,
): string => {
  const date = new Date(str);

  if (isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(localeToUse[locale], options).format(date);
};

const formatBareDay = (isoDay: string, locale = currentLanguage): string =>
  formatDateTime(`${isoDay}T00:00:00Z`, { ...DATE_FORMAT_OPTIONS, timeZone: 'UTC' }, locale);

const formatBerlinDay = (date: Date | string, locale = currentLanguage): string =>
  formatDateTime(date, { ...DATE_FORMAT_OPTIONS, timeZone: CANONICAL_ANCHOR_TIMEZONE }, locale);

const getTimezoneOffsetMs = (utcMs: number, timeZone: string): number => {
  const offsetName = readLongOffsetName(utcMs, timeZone);
  const offsetMatch = /GMT([+-])(\d{2}):(\d{2})/.exec(offsetName);
  if (!offsetMatch) {
    return 0;
  }
  const [, sign, hours, minutes] = offsetMatch;
  return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes)) * 60_000;
};

// Engines older than Chrome 95 and Safari 15.4 throw on `longOffset`. Falling back to no offset stores
// noon UTC, which is still the picked day in Berlin.
const readLongOffsetName = (utcMs: number, timeZone: string): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'longOffset' })
        .formatToParts(new Date(utcMs))
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
};
